import React, { useState } from 'react';
import AssetPickerModal from '../Modal/modal-assets-picker';
import Image from '../Image/image-component';
import VideoComponent from '../Video/video-component';
import SystemDataService from '../../Services/SystemDataService';
import { ToggleButton, ButtonBlack } from '../Buttons/button-component';
import { InputComponent, TextAreaComponent, FileInput } from '../Inputs/input-component';

const RecursiveField = ({ fieldKey, value, onChange, strings, dir, depth = 0 }) => {
    const [assetPickerOpen, setAssetPickerOpen] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [copied, setCopied] = useState(false);

    const isImage = /(image|media|img|logo|cover|photo)/i.test(fieldKey);

    const handleCopy = (text) => {
        navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    // --- ARRAY HANDLER ---
    if (Array.isArray(value)) {
        // Logic to determine the "empty" shape based on the first item in the array
        const getNewItemTemplate = () => {
            if (value.length > 0) {
                const sample = value[0];
                if (typeof sample === 'object' && sample !== null) {
                    // Clone the structure but clear the values
                    const template = {};
                    Object.keys(sample).forEach(key => {
                        if (key === 'id') return; // Don't clone IDs
                        const val = sample[key];
                        if (typeof val === 'boolean') template[key] = true;
                        else if (Array.isArray(val)) template[key] = [];
                        else if (typeof val === 'object' && val !== null) template[key] = {};
                        else template[key] = "";
                    });
                    return template;
                }
            }
            return ""; // Fallback for simple string arrays
        };

        return (
            <div className="flex flex-col gap-4 w-full">
                {value.map((item, index) => (
                    <div key={index} className="relative p-6 bg-[#f9f9f9] dark:bg-neutral-900/50 rounded-xl border border-neutral-100 dark:border-neutral-800 transition-all shadow-sm">
                        <span className="text-[11px] font-bold text-neutral-400 block mb-4 italic">
                            {fieldKey.split(/(?=[A-Z])/).join(" ")} {index + 1}
                        </span>
                        <RecursiveField depth={depth + 1} fieldKey={`${fieldKey}_item`} value={item} onChange={(newVal) => {
                            const newArr = [...value]; newArr[index] = newVal; onChange(newArr);
                        }} strings={strings} dir={dir} />
                        <button 
                            onClick={() => onChange(value.filter((_, i) => i !== index))} 
                            className="absolute top-4 right-4 p-1 text-neutral-300 hover:text-red-500 transition-colors"
                            title={strings.card.delete}
                        >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12" /></svg>
                        </button>
                    </div>
                ))}
                <button 
                    onClick={() => onChange([...value, getNewItemTemplate()])} 
                    className="text-[12px] font-semibold text-blue-600 hover:underline w-fit"
                >
                    {strings.header.addNew}
                </button>
            </div>
        );
    }

    // --- OBJECT HANDLER ---
    if (typeof value === 'object' && value !== null) {
        return (
            <div className="flex flex-col gap-6 w-full">
                {Object.keys(value).map((subKey) => (
                    <div key={subKey} className="w-full">
                        <label className="text-[11px] font-semibold text-neutral-400 block mb-1.5 ml-0.5">
                            {subKey.split(/(?=[A-Z])/).join(" ")}
                        </label>
                        <RecursiveField depth={depth + 1} fieldKey={subKey} value={value[subKey]} onChange={(newVal) => onChange({ ...value, [subKey]: newVal })} strings={strings} dir={dir} />
                    </div>
                ))}
            </div>
        );
    }

    // --- MEDIA ---
    if (isImage) {
        const isVideo = value?.toLowerCase().match(/\.(mp4|webm|mov|ogg)$/);
        const hasValue = value && value !== "";

        return (
            <div className="flex flex-col gap-4 w-full p-4 bg-white dark:bg-neutral-900 border border-neutral-100 dark:border-neutral-800 rounded-xl">
                <div className="relative w-full aspect-video rounded-lg overflow-hidden bg-neutral-50 dark:bg-neutral-950 border border-neutral-100 dark:border-neutral-800 group shadow-inner">
                    {hasValue ? (
                        <>
                            {isVideo ? (
                                <VideoComponent video={value} className="w-full h-full object-contain p-2" />
                            ) : (
                                <Image src={value} wrapperClassName="absolute inset-0 w-full h-full" className="w-full h-full object-contain p-2" />
                            )}
                            <button 
                                onClick={() => onChange("")} 
                                className="absolute top-2 right-2 p-1.5 bg-black/50 hover:bg-red-500 text-white rounded-md transition-all z-30 shadow-lg"
                                title={strings.modal.removeImage}
                            >
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M6 18L18 6M6 6l12 12" /></svg>
                            </button>
                        </>
                    ) : (
                        <div className="w-full h-full flex flex-col items-center justify-center text-neutral-300 font-medium italic text-[11px] gap-2">
                            <svg className="w-8 h-8 opacity-20" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                            <span>{strings.modal.noImageSelected}</span>
                        </div>
                    )}
                </div>

                {/* --- NEW: SOURCE LINK DISPLAY --- */}
                {hasValue && (
                    <div className="flex items-center gap-2 bg-neutral-50 dark:bg-neutral-950 p-2 rounded-lg border border-neutral-100 dark:border-neutral-800">
                        <input 
                            readOnly 
                            value={value} 
                            className="flex-grow bg-transparent text-[10px] font-mono text-neutral-500 outline-none truncate"
                        />
                        <button 
                            onClick={() => handleCopy(value)}
                            className={`p-1.5 rounded-md transition-all ${copied ? 'bg-green-500 text-white' : 'hover:bg-neutral-200 dark:hover:bg-neutral-800 text-neutral-400'}`}
                        >
                            {copied ? (
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" /></svg>
                            ) : (
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>
                            )}
                        </button>
                    </div>
                )}

                <div className="flex flex-col gap-2">
                    <FileInput 
                        label={uploading ? strings.modal.uploading : strings.modal.uploadHelp} 
                        className="!w-full !py-6 bg-neutral-50 dark:bg-neutral-800/50" 
                        onFileSelected={async (file) => {
                            setUploading(true);
                            try {
                                const url = await SystemDataService.uploadMedia(file);
                                if (url) onChange(url);
                            } finally { setUploading(false); }
                        }} 
                    />
                    <div className="flex items-center gap-2 px-2">
                        <div className="h-px bg-neutral-200 dark:bg-neutral-800 flex-grow" />
                        <span className="text-[10px] font-bold text-neutral-400 uppercase">{strings.modal.or}</span>
                        <div className="h-px bg-neutral-200 dark:bg-neutral-800 flex-grow" />
                    </div>
                    <ButtonBlack 
                        onClick={() => setAssetPickerOpen(true)} 
                        className="!w-full !py-3.5 !text-[12px] font-bold !rounded-xl shadow-sm"
                    >
                        {strings.modal.browseGallery}
                    </ButtonBlack>
                </div>
                <AssetPickerModal open={assetPickerOpen} onClose={() => setAssetPickerOpen(false)} onSelect={onChange} dir={dir} strings={strings} />
            </div>
        );
    }

    // --- TEXT / BOOLEAN ---
    if (typeof value === 'boolean') {
        return (
            <div className="flex items-center justify-between p-3.5 bg-neutral-50/50 dark:bg-neutral-800/20 rounded-xl border border-neutral-100 dark:border-neutral-800">
                <span className="text-[13px] font-semibold text-neutral-600 dark:text-neutral-300 capitalize">
                    {fieldKey.split(/(?=[A-Z])/).join(" ")}
                </span>
                <ToggleButton isOn={value} onToggle={onChange} className="scale-90" />
            </div>
        );
    }

    const isBig = fieldKey.toLowerCase().includes('description') || (value?.length > 120);
    return isBig ? (
        <TextAreaComponent 
            value={value || ''} 
            onChange={(e) => onChange(e.target.value)} 
            placeholder={strings.modal.imagePlaceholder} 
            className="!rounded-xl !bg-white dark:!bg-[#0c0c0c] !border-neutral-200 dark:!border-neutral-800 !text-[14px] !p-4 focus:!border-blue-500" 
            rows={3} 
        />
    ) : (
        <InputComponent 
            value={value || ''} 
            onChange={(e) => onChange(e.target.value)} 
            placeholder={strings.modal.imagePlaceholder} 
            className="!rounded-xl !bg-white dark:!bg-[#0c0c0c] !border-neutral-200 dark:!border-neutral-800 !text-[14px] h-12 px-4 focus:!border-blue-500 transition-all" 
        />
    );
};

export default RecursiveField;